import { MdsItem, ProcessedMds, Status, Millsheet, DwgData } from '../types';
import { processMdsData } from './bomProcessor';

// Helper to normalize header strings for comparison
const normalizeHeader = (h: any) => String(h).toLowerCase().replace(/[^a-z0-9]/g, '');

export const parseImdsExcel = (rows: any[][], fileName: string): ProcessedMds | null => {
  if (!rows || rows.length < 2) return null;

  // 1. Detect Header Row
  // We look for a row that contains key IMDS columns like "Level", "Name", "Part No", etc.
  let headerRowIndex = -1;
  const knownHeaders = ['level', 'description', 'partitemno', 'itemmatno', 'substancename', 'casno', 'weight', 'portion'];
  
  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    const rowStr = rows[i].map(normalizeHeader).join(' ');
    let matchCount = 0;
    knownHeaders.forEach(k => {
        if (rowStr.includes(k)) matchCount++;
    });
    
    // If we find at least 3 known headers, assume this is the header row
    if (matchCount >= 3) {
        headerRowIndex = i;
        break;
    }
  }

  if (headerRowIndex === -1) return null;

  const headers = rows[headerRowIndex].map(String);
  const dataRows = rows.slice(headerRowIndex + 1);

  // 2. Map Headers to Fields
  const colMap: Record<string, number> = {};
  headers.forEach((h, i) => {
    const norm = normalizeHeader(h);
    if (norm.includes('level')) colMap['level'] = i;
    else if (norm.includes('description') || norm.includes('articlename') || norm.includes('name')) colMap['name'] = i;
    else if (norm.includes('partitemno') || norm.includes('partno')) colMap['partNo'] = i;
    else if (norm.includes('itemmatno') || norm.includes('materialno')) colMap['matNo'] = i;
    else if (norm.includes('substancename')) colMap['substance'] = i;
    else if (norm.includes('casno')) colMap['cas'] = i;
    else if (norm.includes('weight') || norm.includes('mass')) colMap['weight'] = i;
    else if (norm.includes('portion') || norm.includes('percentage')) colMap['portion'] = i;
    else if (norm.includes('classification')) colMap['class'] = i;
    else if (norm.includes('quantity')) colMap['qty'] = i;
  });

  // If critical columns are missing, abort to fallback
  if (colMap['level'] === undefined) return null;

  // 3. Process Rows
  const items: MdsItem[] = [];
  
  dataRows.forEach((row, idx) => {
    // Skip empty rows
    if (!row || row.length === 0 || row.every(c => !c)) return;

    const levelStr = row[colMap['level']];
    // If level is missing, it might be a continuation or invalid. For now skip if no level.
    if (levelStr === undefined || levelStr === null || String(levelStr).trim() === '') return;

    const level = parseInt(String(levelStr).replace(/[^0-9]/g, ''));
    if (isNaN(level)) return;

    const name = colMap['name'] !== undefined ? String(row[colMap['name']] || '') : '';
    const substance = colMap['substance'] !== undefined ? String(row[colMap['substance']] || '') : '';
    const partNo = colMap['partNo'] !== undefined ? String(row[colMap['partNo']] || '') : '';
    const matNo = colMap['matNo'] !== undefined ? String(row[colMap['matNo']] || '') : '';
    const cas = colMap['cas'] !== undefined ? String(row[colMap['cas']] || '') : '';
    const weightStr = colMap['weight'] !== undefined ? String(row[colMap['weight']] || '0') : '0';
    const weight = parseFloat(weightStr.replace(/[^0-9.]/g, '')) || 0;
    const portionStr = colMap['portion'] !== undefined ? String(row[colMap['portion']] || '0') : '0';
    const portion = parseFloat(portionStr.replace(/[^0-9.]/g, '')) || 0;
    const qtyStr = colMap['qty'] !== undefined ? String(row[colMap['qty']] || '1') : '1';
    const qty = parseFloat(qtyStr.replace(/[^0-9.]/g, '')) || 1;
    const classification = colMap['class'] !== undefined ? String(row[colMap['class']] || '') : '';

    // Determine if it's a component or substance
    // Usually substances have a CAS number or are at a deeper level with substance name
    const isSubstance = !!cas || (!!substance && !partNo);
    
    const displayName = isSubstance ? substance : name;
    const displayPartNo = partNo || matNo || '-';

    // Construct RoHS composition if it is a substance
    const rohsComposition: { [key: string]: string } = {};
    if (isSubstance) {
        const key = cas ? cas.replace(/[^\d]/g, '') : substance.toLowerCase().replace(/\s+/g, '_');
        // Portion is usually %, so * 10000 for PPM
        // But sometimes portion is weight. We assume portion column is percentage if available.
        // If portion is 0-1 (decimal), * 1000000? Usually IMDS exports are %.
        // Let's assume % for now.
        rohsComposition[key] = (portion * 10000).toString();
    }

    const newItem: MdsItem = {
        no: idx + 1,
        level: level,
        part_number_customer: displayPartNo,
        part_number_idbm: '-',
        part_name_customer: displayName || 'Unknown',
        part_name_idbm: '-',
        qty_r: qty,
        qty_l: 0,
        qty_total: qty,
        status: Status.NEW,
        uom: isSubstance ? 'MG' : 'PC',
        berat_level: weight,
        berat_total: weight, // This will be recalculated by processMdsData
        material: matNo || '-',
        millsheet: Millsheet.X,
        manufacturer: '-',
        parent_part_number: '', // Will be filled by hierarchy builder
        imdsData: {
            treeLevel: level,
            name: name,
            substanceName: substance,
            partItemNo: partNo,
            itemMatNo: matNo,
            casNo: cas,
            weight: weight,
            portion: portion,
            classification: classification,
            quantity: qty
        },
        details: {
            pdfs: [],
            customFields: [],
            rohsComposition
        }
    };

    items.push(newItem);
  });

  if (items.length === 0) return null;

  // 4. Wrap in ProcessedMds
  // We use processMdsData to rebuild the tree structure and calculate totals correctly
  const dwg: DwgData = {
    customer: 'IMDS Import',
    dwg_number: fileName.replace(/\.[^/.]+$/, ""), // Remove extension
    hr_mark: '-',
    status: Status.NEW
  };

  const { data: processedData } = processMdsData(dwg, items);
  
  if (processedData) {
    return {
        ...processedData,
        uploadedAt: new Date().toISOString()
    };
  }

  return null;
};
